import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  CellClickedEvent,
  ColDef,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ICellRendererParams,
  MenuItemDef,
  RowClassParams,
  RowClassRules,
  ValueGetterParams,
} from 'ag-grid-community';
import * as Highcharts from 'highcharts';
import { debounceTime, forkJoin, Observable, Subscription } from 'rxjs';
import { PhoneReportsWidgetComponent } from '../../../other/phone-reports/phone-reports-widget/phone-reports-widget.component';
import { AGTableBase } from '../../../shared/components/table/ag-table-base';
import { ExportFile } from '../../../shared/models/export.model';
import {
  PhoneReportByTestIdInfo,
  PhoneReportCreate,
  PhoneReportsByTestsIdsRequestDto,
  PhoneReportTestType,
} from '../../../shared/models/phone-report.model';
import { Session } from '../../../shared/models/session.model';
import { SmppDelay } from '../../../shared/models/smpp-delay.model';
import { Supplier } from '../../../shared/models/supplier.model';
import { TestDetails, TestResult, TestResultAction, TestResultsCollection } from '../../../shared/models/test-result.model';
import { AuthUser, Role } from '../../../shared/models/user.model';
import { BrowserUtils } from '../../../shared/services/browser-utils';
import { CustomUtils } from '../../../shared/services/custom-utils';
import { DlrRecStatusRenderUtils } from '../../../shared/services/dlr-rec-status-render-utils';
import { ExportService } from '../../../shared/services/export.service';
import { AllRequestParams, LiveNumberTestingService, SearchParams } from '../../../shared/services/live-number-testing.service';
import { LiveRealtimeService } from '../../../shared/services/live-realtime-service';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { DialogRef, ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PhoneReportUtils } from '../../../shared/services/phone-report-utils';
import { PhoneReportService } from '../../../shared/services/phone-report.service';
import { ReimburseUtils } from '../../../shared/services/reimburse-utils';
import { SessionsService } from '../../../shared/services/sessions.service';
import { TestResultUtils } from '../../../shared/services/test-result-utils';
import { UsersService } from '../../../shared/services/users.service';
import { LntSearchFormComponent } from '../lnt-search-form/lnt-search-form.component';
import { LntActionsComponent } from './lnt-actions/lnt-actions.component';

declare const moment: any;

@Component({
  standalone: false,
  selector: 'app-lnt-results-table',
  templateUrl: 'lnt-results-table.component.html',
  styleUrls: ['lnt-results-table.component.scss'],
})
export class LntResultsTableComponent extends AGTableBase implements OnInit, OnDestroy {
  private static readonly DEFAULT_PAGE_SIZE = 20;
  private static readonly ADMIN_PAGE_SIZE = 1000;
  private static readonly FLASH_CALL_TEXT = '—';
  @ViewChild(PhoneReportsWidgetComponent, { static: false }) phonenumberReportWidget: PhoneReportsWidgetComponent;
  @ViewChild('exportModalTpl', { read: TemplateRef, static: false }) exportModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('copyModalTpl', { read: TemplateRef, static: false }) copyModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('senderModalTpl', { read: TemplateRef, static: false }) senderModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('textModalTpl', { read: TemplateRef, static: false }) textModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('smscModalTpl', { read: TemplateRef, static: false }) smscModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('connectionModalTpl', { read: TemplateRef, static: false }) connectionModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('latencyModalTpl', { read: TemplateRef, static: false }) latencyModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('commentModalTpl', { read: TemplateRef, static: false }) commentModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('reimburseModalTpl', { read: TemplateRef, static: false }) reimburseModalTpl: TemplateRef<HTMLTemplateElement>;

  @Input() taskId: number;
  @Input() batchRepeatRows: TestResult[] = [];
  @Input() compact = false;
  @Input() pauseUpdate = false;
  @Output() changeBatch = new EventEmitter<TestResult[]>();
  @Output() repeat = new EventEmitter<TestResult>();
  @Output() actions = new EventEmitter<unknown>();

  // ng grid
  columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'batch',
      checkboxSelection: true,
      maxWidth: 40,
      headerCheckboxSelection: true,
      pinned: 'left',
      lockPinned: true,
      lockPosition: 'left',
      lockVisible: true,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
    },
    {
      headerName: 'ID',
      field: 'id',
      hide: true,
      flex: 0.5,
      cellRenderer: (params: ICellRendererParams) => {
        return `<div class="one-line">${params.data.id}</div>`;
      },
      onCellClicked: (event: CellClickedEvent) => {
        const id = event.data.id;
        this.clipBoard.copy(id);
      },
    },
    {
      headerName: 'Date',
      field: 'date',
      minWidth: 90,
      maxWidth: 90,
      flex: 0.6,
      headerTooltip: 'Date and time when the test was triggered, in the browser’s time zone',
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return TestResultUtils.renderDate(_);
      },
    },
    {
      headerName: 'UTC Date',
      field: 'utcTimestamp',
      minWidth: 90,
      maxWidth: 90,
      flex: 0.6,
      headerTooltip: 'UTC date for test created date.',
      hide: true,
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return CustomUtils.formatDateIntoUTC(_.createdAt);
      },
    },
    {
      headerName: 'Country(MCC)/Network(MNC)',
      field: 'destinationDto',
      flex: 1.5,
      headerTooltip: 'The destination to which the test was sent',
      cellRenderer: (params: ICellRendererParams) => {
        return `<div class="two-lines">${params.value ? this.formatCountry(params.value) : ''}</div>`;
      },
    },
    {
      headerName: 'DLR/Rec. Status',
      field: 'dlrRecStatus',
      minWidth: 90,
      maxWidth: 90,
      flex: 0.6,
      headerTooltip: 'DLR status - Delivery Report received from the supplier via SMPP, Rec.status - Receipt Status received from our test number.',
      cellClass: 'align-items-center justify-content-center',
      cellRenderer: (params: ICellRendererParams) => {
        const row = params.data;
        return this.formatDlrRecStatus(row);
      },
    },
    {
      headerName: 'Sender ID',
      field: 'senderDelivered',
      minWidth: 130,
      flex: 1,
      tooltipField: 'senderDelivered',
      headerTooltip: 'Sender ID of the delivered SMS (submitted Sender for undelivered SMS)',
      valueGetter: (params: ValueGetterParams<TestResultAction>) =>
        TestResultUtils.isFlashCallDelivered(params.data) ? params.data.flashCallCallerId : params.data.senderDelivered,
      cellRenderer: (params: ICellRendererParams) => {
        const row = params.data;
        if (TestResultUtils.isFlashCallDelivered(row)) {
          return TestResultUtils.renderFlashCallCallerId(row.flashCallCallerId);
        }
        return TestResultUtils.renderSenderDelivered(row.senderDelivered, row.senderSent, 'clickable');
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'Submitted Sender ID',
      field: 'senderSent',
      hide: true,
      flex: 0.7,
      tooltipField: 'senderSent',
      headerTooltip: 'Sender ID of the sent SMS',
      cellRenderer: (params: ICellRendererParams) => {
        const row = params.data;
        return this.formatSenderSent(row);
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'Text',
      field: 'textDeliveredFull',
      flex: 1.5,
      headerTooltip: 'Text of the delivered SMS (submitted Text for undelivered SMS)',
      valueGetter: (params: ValueGetterParams<TestResultAction>) =>
        TestResultUtils.isFlashCallDelivered(params.data) ? LntResultsTableComponent.FLASH_CALL_TEXT : params.data.textDeliveredFull,
      cellRenderer: (params: ICellRendererParams) => {
        if (TestResultUtils.isFlashCallDelivered(params.data)) {
          return TestResultUtils.renderFlashCallText();
        }
        return TestResultUtils.renderLntText(params.data.textDeliveredFull, params.data.textSentBackend, 'clickable');
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'Phone/SMSC',
      field: 'phoneSmsc',
      flex: 1,
      minWidth: 130,
      headerTooltip: 'Test number to which the test SMS was sent',
      cellRenderer: (params: ICellRendererParams) => {
        const row = params.data;
        return TestResultUtils.renderLntPhoneSmscInfo(row.destinationDto, row.smscInfoDto, 'clickable');
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'Phone',
      field: 'phone',
      flex: 1,
      minWidth: 130,
      hide: true,
      headerTooltip: 'Test number to which the test SMS was sent',
      cellRenderer: (params: ICellRendererParams) => {
        const row = params.data;
        return this.formatPhone(row);
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'SMSC',
      field: 'smsc',
      flex: 1,
      minWidth: 130,
      hide: true,
      cellRenderer: (params: ICellRendererParams) => {
        const row = params.data;
        return this.formatSmsc(row);
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'Supplier',
      field: 'supplierDto',
      headerTooltip: 'Supplier, via which the test was sent',
      cellRenderer: (params: ICellRendererParams) => {
        const row = params.data;
        return `<div class="two-lines clickable">${TestResultUtils.renderConnection(row)}</div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'DLR/Rec. delay',
      field: 'delay',
      minWidth: 90,
      maxWidth: 90,
      flex: 0.6,
      headerTooltip: 'Delays in getting the SMPP DLR status and Receipt status',
      cellRenderer: (params: ICellRendererParams) => {
        const row = params.data;
        return `<div class="two-lines clickable">${TestResultUtils.renderDelay(row.receiptDelay, row.smppDelay)}</div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'User',
      field: 'user',
      flex: 0.5,
      headerTooltip: 'User that triggered the test',
      tooltipField: 'user',
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.user ? (_.user.username ? _.user.username : _.user.email) : 'N/A'}</div>`;
      },
    },
    {
      headerName: 'Price',
      field: 'price',
      hide: true,
      flex: 0.4,
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.price > 0 ? `${_.price / 1000}` : '0'}</div>`;
      },
    },
    {
      headerName: 'Comment',
      field: 'commentText',
      flex: 1,
      hide: true,
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.commentText ? _.commentText : ''}</div>`;
      },
    },
    {
      headerName: 'Actions',
      field: 'actions',
      maxWidth: 100,
      minWidth: 100,
      pinned: 'right',
      lockPinned: true,
      lockPosition: 'right',
      lockVisible: true,
      suppressColumnsToolPanel: false,
      suppressMenu: false,
      suppressAutoSize: true,
      tooltipField: 'actions',
      headerTooltip: 'Right click on a row to see more options.',
      headerClass: 'action-cell',
      cellClass: 'action-cell',
      cellRenderer: LntActionsComponent,
      onCellClicked: (event: CellClickedEvent) => this.onActionClick(event),
    },
  ];
  rowData!: TestResultAction[];
  rowClassRules: RowClassRules = {
    ...this.rowClassRules,
    'flash-call-row': (params: RowClassParams<TestResultAction>) => TestResultUtils.isFlashCallDelivered(params.data),
  };
  // ng grid

  RESULTS_UPDATE_FREQUENCY = 40 * 1000; // 40 seconds
  exportIsArchive = 0;
  commentModel;
  batchSelectedIds = new Set<number>();
  currentSection = '';
  testCaseDetails: TestDetails[] = [];
  updateTimer: any;
  connectionSpinner = false;
  latencySpinner = false;
  connection: any = null;
  isAdmin = false;
  exportSize = 500;
  exportSizes = [500, 1000, 3000, 5000, 'All'];
  exportData: ExportFile = {} as ExportFile;
  exportSpinner = false;
  loading = false;
  diff = {
    title: '',
    title1: '',
    value1: '',
    title2: '',
    value2: '',
    result: '',
    senderPduHex: '',
  };
  flashCallCallerIdHtml: string = null;
  smscData = {
    smsc: {
      phone: '',
      phoneFromPdu: '',
      country: '',
      provider: '',
      mcc: '',
      mnc: '',
    },
    test: {
      phone: '',
      country: '',
    },
    country: '',
    isError: false,
  };

  searchParams: SearchParams;

  resultMap = [];
  user: AuthUser;

  phoneReportsInfo: PhoneReportByTestIdInfo[] = [];
  isPhoneReportsLoading = false;
  private phoneReportsSubscription?: Subscription;

  commentModal: DialogRef;
  supplier: Supplier;
  session: Session;
  statusUpdates$: Subscription;

  Highcharts: typeof Highcharts = Highcharts;
  smppChartOptions: Highcharts.Options;
  smsChartOptions: Highcharts.Options;
  selectedTestIdForCharts: number;
  smppChartMessageTitle?: string;
  smppChartMessageDesc?: string;
  smppResponseUnavailable = false;
  smsChartMessageTitle?: string;

  constructor(
    public router: Router,
    public notificationService: NotificationService,
    public service: LiveNumberTestingService,
    public connectionService: SessionsService,
    public userService: UsersService,
    public localStorage: LocalStorageService,
    public modal: ModalService,
    public exportService: ExportService,
    public clipBoard: Clipboard,
    public liveRealtimeService: LiveRealtimeService,
    public phoneReportService: PhoneReportService
  ) {
    super();
  }

  ngOnInit() {
    this.userService
      .getAuthUser()
      .then(user => {
        this.user = user;
        this.isAdmin = user.role === Role.ADMIN;
        this.init();
      })
      .catch(e => {});
    this.actionSubscription = this.service.action$.subscribe(action => this.onAction(action));
    this.columnChange$.pipe(debounceTime(1000)).subscribe(() => {
      const currentColumnState = this.gridApi.getColumnState();
      this.localStorage.set(`lnt_table_state_${this.user.id}`, currentColumnState);
    });
  }

  init() {
    const hideColumns = [];
    if (this.user.role === Role.SUB && !this.user.showAllResults) {
      hideColumns.push('User');
    }
    if (this.compact) {
      hideColumns.push('');
      hideColumns.push('ID');
      hideColumns.push('User');
      hideColumns.push('Price');
    }
    this.columnDefs = this.columnDefs.filter(c => !hideColumns.includes(c.headerName));
    const savedUserSearchDetails = this.localStorage.get(`lnt_user_search_${this.user.id}_search`);
    const savedPageSize = this.localStorage.get(`lnt_results_table_component_size_${this.user.id}`);
    this.paginationPageSize = savedPageSize || (this.isAdmin ? LntResultsTableComponent.ADMIN_PAGE_SIZE : LntResultsTableComponent.DEFAULT_PAGE_SIZE);
    this.sizes = this.isAdmin ? [...this.sizes, LntResultsTableComponent.ADMIN_PAGE_SIZE] : this.sizes;
    if (savedUserSearchDetails) {
      this.searchParams = savedUserSearchDetails;
    }

    this.scheduleUpdate();

    this.listenRealTimeUpdates();
  }

  scheduleUpdate() {
    this.updateTimer = setTimeout(() => {
      if (this.loading) {
        return;
      }

      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }

      if (!this.user || !this.userService?.authUser) {
        return;
      }

      const savedUserSearchDetails = this.localStorage.get(`lnt_user_search_${this.user.id}_search`);

      const isShowOnlyMyTestActive = !!(
        savedUserSearchDetails &&
        Array.isArray(savedUserSearchDetails.userIds) &&
        savedUserSearchDetails.userIds.length > 0 &&
        savedUserSearchDetails.userIds[0] === this.userService.authUser.id
      );
      if (isShowOnlyMyTestActive) {
        return;
      }

      const isSubAccountWithFalsyShowAllResults = this.user.role === Role.SUB && !this.user.showAllResults;
      if (isSubAccountWithFalsyShowAllResults) {
        return;
      }

      this.update(false);
    }, this.RESULTS_UPDATE_FREQUENCY);
  }

  formatCountry(c, icon = true) {
    return TestResultUtils.renderDestination(c, icon);
  }

  formatDateIntoUTC(_: TestResultAction): string {
    const utcDate = moment.utc(_.createdAt).format('DD/MM/YY HH:mm:ss').split(' ');
    return `<div class="two-lines"><span>${utcDate[1]}<br>${utcDate[0]}</span></div>`;
  }

  formatSenderSent(row: TestResultAction) {
    const text = BrowserUtils.escapeHtml(row.senderSent);
    return `<div class="two-lines"><span>${text}</span></div>`;
  }

  formatDlrRecStatus(row: TestResultAction) {
    return DlrRecStatusRenderUtils.formatDlrRecStatus(row.testStatus, row.messageState, row.channel, { class: 'clickable-wrapper' });
  }

  formatPhone(row: TestResultAction): string {
    return `<div class="two-lines clickable"><span>${row.destinationDto.phone || '-'}</span></div>`;
  }

  formatSmsc(row: TestResultAction): string {
    const phone = row.smscInfoDto?.phone || '-';
    return phone === '-' ? '-' : `<div class="two-lines clickable"><span>${phone}</span></div>`;
  }

  changeSize($event, size) {
    this.paginationPageSize = size;
    this.localStorage.set(`lnt_results_table_component_size_${this.user.id}`, size);
    this.update();
  }

  handleBatchRepeat() {
    this.loading = true;
    this.rowData = this.batchRepeatRows;
    this.gridApi.updateGridOptions({ rowData: this.rowData });
    this.totalRowsCount = this.batchRepeatRows.length;
    this.loading = false;
  }

  shouldSearch(): boolean {
    for (const key in this.searchParams) {
      if (!['sort', 'size', 'page'].includes(key)) {
        const value = this.searchParams[key];
        if (key === 'userIds' && value.length === 1 && value[0] === this.user.id) {
          // because for Show only my tests we plan on calling test results api ...
          continue;
        }
        if (value?.length > 0 || value) {
          return true;
        }
      }
    }
    return false;
  }

  listenRealTimeUpdates() {
    this.statusUpdates$ = this.liveRealtimeService.connect().subscribe({
      next: data => {
        this.gridApi.forEachNode(node => {
          if (data.id === node.data.id) {
            const updatedData = { ...node.data, ...data };
            updatedData.delay = TestResultUtils.renderDelay(updatedData.receiptDelay, updatedData.smppDelay);
            if (data.messageState || data.testStatus) {
              updatedData.dlrRecStatus = this.formatDlrRecStatus(updatedData);
            }
            if (data.smscInfoDto) {
              updatedData.phoneSmsc = data.smscInfoDto;
            }
            node.updateData(updatedData);
          }
        });
      },
      error: e => {
        console.error('Error: ', e);
      },
      complete: () => console.info('Connection closed'),
    });
  }

  getPrimaryRequest(): Observable<TestResultsCollection> {
    const params = new AllRequestParams();
    params.size = this.paginationPageSize;
    params.page = this.currentPageNumber - 1;

    if (this.shouldSearch()) {
      return this.service.searchResults(params, this.searchParams);
    } else {
      if (this.searchParams?.userIds) {
        params.onlyMyTests = this.searchParams.userIds.length > 0;
      }
      return this.service.results(params);
    }
  }

  getBackupRequest(): Observable<TestResultsCollection> | undefined {
    const testIdsTobeQueriedForBackUp = [
      ...new Set(
        this.rowData
          ?.filter(test => test.isBackupShown)
          .flatMap(test => [test.id, test.relatedTestId])
          .filter((id): id is number => typeof id === 'number') || []
      ),
    ].map(id => `${id}`);
    if (testIdsTobeQueriedForBackUp?.length) {
      const searchParams = {
        createdAtFrom: null,
        createdAtTo: null,
        destinations: [],
        ids: testIdsTobeQueriedForBackUp,
        telqIds: null,
        messageStates: [],
        senders: [],
        sessionSupplierIds: [],
        testStatuses: [],
        userIds: [],
        taskId: null,
      };
      const backupParams = new AllRequestParams();
      backupParams.size = 0;
      backupParams.page = 0;
      return this.service.searchResults(backupParams, searchParams);
    }
  }

  handleSuccess(results) {
    const primaryResults = results.primaryRequest;
    const backUpResults = results.backupRequest;
    const testResults: TestResultAction[] = [];
    const testIds = primaryResults.content.map(result => result.id).filter((id): id is number => typeof id === 'number');

    const expandedTestIds = new Set(this.rowData ? this.rowData.filter(test => test.isBackupShown).map(test => test.id) : []);
    const availableResultsById = new Map<number, TestResultAction>();
    [...primaryResults.content, ...(backUpResults?.content || [])].forEach((result: TestResultAction) => {
      if (typeof result.id === 'number') {
        availableResultsById.set(result.id, result);
      }
    });
    const addedTestIds = new Set<number>();

    primaryResults.content.forEach((res: TestResultAction) => {
      if (typeof res.id === 'number' && addedTestIds.has(res.id)) {
        return;
      }

      const expandedTestId = expandedTestIds.has(res.id) ? res.id : res.relatedTestId;
      const isPairExpanded = expandedTestIds.has(expandedTestId);
      const relatedTest = res.relatedTestId ? availableResultsById.get(res.relatedTestId) : undefined;

      if (isPairExpanded && relatedTest) {
        this.prepareTestPairForDisplay(res, relatedTest, expandedTestId).forEach(test => {
          if (typeof test.id !== 'number' || !addedTestIds.has(test.id)) {
            testResults.push(test);
            if (typeof test.id === 'number') addedTestIds.add(test.id);
          }
        });
        return;
      }

      this.prepareTestResultForDisplay(res);
      testResults.push(res);
      if (typeof res.id === 'number') addedTestIds.add(res.id);
    });

    this.totalRowsCount = primaryResults.totalElements;
    this.rowData = testResults;
    this.gridApi.updateGridOptions({ rowData: this.rowData });
    setTimeout(() => {
      this.gridApi.forEachNode(node => {
        if (this.batchSelectedIds.has(node.data.id)) {
          node.setSelected(true);
        }
      });
    }, 0);

    this.loading = false;
    this.liveRealtimeService.sendMessage({ trackedTestIds: testIds });
    this.scheduleUpdate();
  }

  handleError(e) {
    this.loading = false;
    const requestMessage = e.status === 408 ? 'The request timed out. Try using a shorter time range or narrowing your query.' : e.statusText;

    this.notificationService.error({
      title: 'Live number testing',
      message: 'An error occurred while loading the results',
      serviceName: 'NTC',
      requestMessage,
      requestCode: e.status,
      ts: e.timestamp ? e.timestamp : null,
    });
    this.scheduleUpdate();
  }

  update(loading = true) {
    clearTimeout(this.updateTimer);
    if (!this.user.enabled) {
      return;
    }
    if (this.batchRepeatRows.length > 0) {
      this.handleBatchRepeat();
      return;
    }

    this.loading = loading;

    if (this.taskId) {
      this.searchParams = {
        taskIds: [this.taskId],
        userIds: [],
        commentText: null,
        commentDateFrom: null,
        commentDateTo: null,
        text: '',
      };
    }

    const requests: {
      primaryRequest?: Observable<TestResultsCollection>;
      backupRequest?: Observable<TestResultsCollection>;
    } = {
      primaryRequest: this.getPrimaryRequest(),
      backupRequest: this.getBackupRequest(),
    };

    const filteredRequests = Object.entries(requests)
      .filter(([, val]) => val !== undefined)
      .reduce((acc, [key, val]) => {
        acc[key] = val;
        return acc;
      }, {});

    forkJoin(filteredRequests).subscribe({
      next: results => {
        this.handleSuccess(results);
        this.fetchPhoneReportsInfo(this.rowData);
      },
      error: e => this.handleError(e),
    });
  }

  onAction(event: { name: string; column: string; row: TestResultAction }) {
    this.actions.emit(event);
    if (event.name === 'copy') {
      this.testCaseDetails = [];
      this.modal.alert().dialogClass('modal-dialog large-modal').component(this.copyModalTpl).open();
      this.service.getResultDetailsText(event.row).subscribe(
        details => {
          this.testCaseDetails = this.getFormattedData(details);
          this.currentSection = this.testCaseDetails[0].id;
        },
        e => {
          console.error(e);
        }
      );
    }

    if (event.name === 'showPhoneNumberReport') {
      this.openPhoneReport(event.row.id);
    }

    if (event.name === 'addPhoneNumberReport') {
      this.phonenumberReportWidget.open({
        phone: event.row.destinationDto.phone,
        testId: event.row.id!,
        testType: PhoneReportTestType.LNT,
      });
    }

    if (event.name === 'appscriptsUser') {
      this.router.navigate(['/appscripts/numbers'], { queryParams: { phonenumber: event.row.destinationDto.phone } });
    }

    if (event.name === 'backUpShow') {
      this.service.one(event.row.relatedTestId).subscribe(
        backup => this.insertBackup(backup),
        () => {
          this.notificationService.error('Error fetching the backup test.', 'Backup test');
        }
      );
    }

    if (event.name === 'backUpHide') {
      this.removeBackup(event.row);
    }

    if (event.name === 'repeat') {
      this.repeat.emit(event.row);
    }

    if (event.name === 'reimburse') {
      this.reimburseTest(event.row);
    }

    if (event.name === 'comment') {
      const modal = comment => {
        this.commentModel = {
          testId: event.row.id,
          commentId: comment ? comment.id : null,
          text: comment ? comment.text : '',
          changedAt: comment ? new Date(comment.changedAt) : null,
          changedUser: comment ? comment.user : null,
        };
        this.commentModal = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.commentModalTpl).open();
      };
      if (event.row.commentId) {
        this.loading = true;
        this.service.getCommentById(event.row.id, event.row.commentId).subscribe(
          comment => {
            modal(comment);
            this.loading = false;
          },
          error => {
            this.notificationService.error({
              title: 'Test comments',
              message: 'An error occurred while receiving text comment',
              serviceName: 'NTC',
              requestMessage: error.statusText,
              requestCode: error.status,
              ts: error.timestamp ? error.timestamp : null,
            });
            this.loading = false;
          }
        );
      } else {
        modal(null);
      }
    }
  }

  reimburseTest(row: TestResultAction): void {
    const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.reimburseModalTpl).open();
    dialogRef.result.then(result => {
      if (result) {
        this.loading = true;
        this.service.reimburse([row.id]).subscribe({
          next: () => {
            this.notificationService.success('Test cases reimbursed', 'Live Number Testing');
            this.update();
          },
          error: e => {
            this.notificationService.error({
              title: 'Reimburse test',
              message: 'An error occurred while reimbursing the test',
              serviceName: 'NTC',
              requestMessage: e.statusText,
              requestCode: e.status,
              ts: e.timestamp ? e.timestamp : null,
            });
            this.loading = false;
          },
        });
      } else {
        this.notificationService.info('Reimbursement canceled', 'Live Number Testing');
      }
    });
  }

  insertBackup(backup: TestResultAction) {
    const parent: TestResultAction = this.rowData.find(row => row.relatedTestId === backup.id);
    if (!parent) return;
    const pair = this.prepareTestPairForDisplay(parent, backup, parent.id);
    const pairIds = new Set(pair.map(test => test.id));
    const index = this.rowData.findIndex(row => pairIds.has(row.id));

    this.rowData = this.rowData.filter(row => !pairIds.has(row.id));
    this.rowData.splice(index, 0, ...pair);
    this.gridApi.updateGridOptions({ rowData: this.rowData });
  }

  private prepareTestPairForDisplay(firstTest: TestResultAction, secondTest: TestResultAction, toggleTestId: number): TestResultAction[] {
    const originalTest = firstTest.retry ? secondTest : firstTest;
    const backupTest = firstTest.retry ? firstTest : secondTest;
    const toggleTest = firstTest.id === toggleTestId ? firstTest : secondTest;
    const relatedTest = toggleTest === firstTest ? secondTest : firstTest;

    this.prepareTestResultForDisplay(originalTest);
    this.prepareTestResultForDisplay(backupTest);
    backupTest.isBackup = true;
    toggleTest.isBackupShown = true;
    relatedTest.hideRelatedTestAction = true;

    return [originalTest, backupTest];
  }

  private prepareTestResultForDisplay(test: TestResultAction): void {
    test.createdAtFormat = moment(test.createdAt).format('DD/MM/YY HH:mm:ss').split(' ');
    test.isBackupShown = false;
    test.isBackup = false;
    test.hideRelatedTestAction = false;
  }

  removeBackup(parent: TestResultAction) {
    parent.isBackupShown = false;
    parent.isBackup = false;
    parent.hideRelatedTestAction = false;
    const index = this.rowData.indexOf(parent);

    this.rowData.splice(index, 1, parent);
    this.rowData = this.rowData.filter(row => row.id !== parent.relatedTestId);
    this.gridApi.updateGridOptions({ rowData: this.rowData });
  }

  onClickExport() {
    this.exportSize = 0;
    this.exportSpinner = false;
    this.exportData = {} as ExportFile;
    this.modal.alert().component(this.exportModalTpl).open();
  }

  onAfterSaveComment() {
    if (this.commentModal) {
      this.commentModal.close();
    }
    this.update(true);
  }

  fetchPhoneReportsInfo(testResults: TestResultAction[]) {
    const testIds = testResults.map(test => test.id).filter((id): id is number => typeof id === 'number');

    if (testIds.length === 0) {
      return;
    }

    const dto: PhoneReportsByTestsIdsRequestDto = {
      testIds,
      testType: PhoneReportTestType.LNT,
    };

    if (this.phoneReportsSubscription) {
      this.phoneReportsSubscription.unsubscribe();
    }

    this.isPhoneReportsLoading = true;

    this.phoneReportsSubscription = this.phoneReportService.getPhoneReportsInfoByTestIds(dto).subscribe({
      next: reportInfos => {
        this.phoneReportsInfo = reportInfos;
        this.isPhoneReportsLoading = false;
      },
      error: e => {
        this.isPhoneReportsLoading = false;

        this.notificationService.error({
          title: 'Phone reports',
          message: 'An error occurred while fetching phone reports for LNT tests',
          serviceName: 'GATEWAY',
          requestMessage: e.statusText,
          requestCode: e.status,
          ts: e.timestamp ? e.timestamp : null,
        });
      },
    });
  }

  export(limit) {
    this.exportSpinner = true;
    let params = this.searchParams;
    const ids = LntSearchFormComponent.getSaveUserIds(this.localStorage, this.user);
    if (!this.searchParams && ids.length) {
      params = { userIds: ids };
    }

    this.exportService
      .export('lnt', limit === 'All' ? null : limit, params ? params : {})
      .then(exportData => {
        this.exportData = exportData;
        this.exportSpinner = false;
      })
      .catch(error => {
        this.notificationService.error({
          title: 'Live number testing',
          message: 'An error occurred while export tests',
          serviceName: 'NTC',
          requestMessage: error.statusText,
          requestCode: error.status,
          ts: error.timestamp ? error.timestamp : null,
        });
        this.exportSpinner = false;
      });
  }

  formatStatus(s, row) {
    return [row.smppStatus, row.testStatus].join('<br>');
  }

  openSummaryModal(dialogRef: DialogRef, row: TestResultAction) {
    dialogRef.result.then(result => {
      if (result === 'viewSummary') {
        this.service.announceAction({ name: 'copy', row: row, column: 'action' });
      }
    });
  }

  onCellClick(event: CellClickedEvent) {
    if (event.colDef.field === 'textDeliveredFull' && !TestResultUtils.isFlashCallDelivered(event.data)) {
      const value1 = event.data.textSentBackend;
      const value2 = event.data.textDeliveredFull;
      this.diff = { ...CustomUtils.createDiffObject('Text sent', value1, 'Text delivered', value2, true, event.data.telqId), senderPduHex: null };
      const dialogRef = this.modal.alert().component(this.textModalTpl).open();
      this.openSummaryModal(dialogRef, event.data);
    }

    if (event.colDef.field === 'senderDelivered' && TestResultUtils.isFlashCallDelivered(event.data)) {
      this.openFlashCallSenderModal(event.data);
    } else if (event.colDef.field === 'senderDelivered') {
      const value1 = event.data.senderSent;
      const value2 = event.data.senderDelivered;
      this.flashCallCallerIdHtml = null;
      this.diff = { ...CustomUtils.createDiffObject('Sender ID sent', value1, 'Sender ID delivered', value2, false), senderPduHex: null };
      const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.senderModalTpl);
      if (value1 !== value2 && event.data.channel == 'sms') {
        this.service.pduDetails(event.data.id).subscribe({
          next: pdu => {
            const pduData = pdu.deliveredSmsPduData?.pdusDelivered[0];
            const parsedPdu = pduData
              ? this.service.parsePdu(pduData)
              : {
                  sender: '',
                  sender_type: '',
                };
            const senderType = parsedPdu.sender_type;
            let decodedSender = parsedPdu.sender as string;
            if (senderType.charAt(0) == 'D' && decodedSender.endsWith('@')) {
              decodedSender = decodedSender.substring(0, decodedSender.length - 1);
            }
            this.diff.senderPduHex = decodedSender !== value2 ? decodedSender : null;
            dialogRef.open();
          },
        });
      } else {
        this.diff.senderPduHex = null;
        dialogRef.open();
      }
      this.openSummaryModal(dialogRef, event.data);
    }

    if ((event.colDef.field === 'phoneSmsc' || event.colDef.field === 'phone' || event.colDef.field === 'smsc') && event.data.smscInfoDto) {
      const hlr = event.data.smscInfoDto;
      this.smscData.test.phone = event.data.destinationDto.phone;
      this.smscData.test.country = event.data.destinationDto.countryName;
      if (!hlr.phone) {
        this.service.pduDetails(event.data.id).subscribe({
          next: pdu => {
            const pduData = pdu.deliveredSmsPduData?.pdusDelivered[0];
            const phoneFromPdu = pduData ? this.service.parsePdu(pduData).smsc : null;
            this.smscData.smsc = {
              phone: null,
              phoneFromPdu,
              country: null,
              provider: null,
              mcc: null,
              mnc: null,
            };
            const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.smscModalTpl).open();
            this.openSummaryModal(dialogRef, event.data);
          },
        });
      } else {
        this.smscData.smsc.phone = hlr.phone;
        this.smscData.smsc.phoneFromPdu = null;
        this.smscData.smsc.country = hlr.countryName;
        this.smscData.isError = hlr.countryName !== event.data.destinationDto.countryName;
        this.smscData.smsc.mnc = hlr.mnc ? hlr.mnc : null;
        this.smscData.smsc.mcc = hlr.mcc ? hlr.mcc : null;
        this.smscData.smsc.provider = hlr.providerName ? hlr.providerName : null;
        const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.smscModalTpl).open();
        this.openSummaryModal(dialogRef, event.data);
      }
    }

    if (event.colDef.field === 'supplierDto') {
      this.connectionSpinner = true;
      this.connectionService.one(event.data.sessionId).subscribe(
        session => {
          this.supplier = event.data.supplierDto;
          this.session = session;
          this.connectionSpinner = false;
        },
        () => (this.connectionSpinner = false)
      );
      this.modal.alert().component(this.connectionModalTpl).open();
    }

    if (event.colDef.field === 'delay') {
      const testId = event.data.id;
      this.latencySpinner = true;
      this.smppResponseUnavailable = false;
      this.smppChartOptions = null;
      this.smsChartOptions = null;
      this.service.getSmppDelayByTestId(testId).subscribe({
        next: response => {
          if (response) {
            this.smppChartMessageTitle = null;
            this.smppChartMessageDesc = null;
            this.smsChartMessageTitle = null;
            this.smppChartOptions = null;
            this.smsChartOptions = null;
            this.populateSmppChart(event, response);
            this.populateSmsChart(event, response);
          } else {
            this.smppChartMessageTitle = 'No data available for this test.';
            this.smppChartMessageDesc = 'Please note that this feature is still in development for multi-part SMS.';
            this.latencySpinner = false;
          }
          this.modal
            .alert()
            .dialogClass('modal-dialog extra-large-modal')
            .component(this.latencyModalTpl)
            .open()
            .onDestroy.subscribe(() => {
              this.smppChartOptions = null;
              this.smsChartOptions = null;
            });
        },
        error: () => {
          this.smppChartMessageTitle = 'No data available for this test.';
          this.smppChartMessageDesc = 'Please note that this feature is still in development for multi-part SMS.';
          this.latencySpinner = false;
          this.modal
            .alert()
            .dialogClass('modal-dialog extra-large-modal')
            .component(this.latencyModalTpl)
            .open()
            .onDestroy.subscribe(() => {
              this.smppChartOptions = null;
              this.smsChartOptions = null;
            });
        },
        complete: () => {
          this.latencySpinner = false;
        },
      });
    }
  }

  openFlashCallSenderModal(row: TestResultAction): void {
    this.diff = {
      ...CustomUtils.createDiffObject('Sender ID sent', row.senderSent, 'Sender ID delivered', null, false),
      value2: '<span class="text-neutral-n6">&mdash;</span>',
      senderPduHex: null,
    };
    this.flashCallCallerIdHtml = TestResultUtils.highlightTestIdInCallerId(row.flashCallCallerId, row.telqId);
    const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.senderModalTpl).open();
    this.openSummaryModal(dialogRef, row);
  }

  populateSmppChart(event: CellClickedEvent, response: SmppDelay): void {
    this.selectedTestIdForCharts = event.data.id;
    const testCreatedTimestamp = event.data.createdAt;
    const smppSubmittedTimestamp = response.submitSmTimestamp;
    const smppResponseTimestamp = response.submitSmResponseTimestamp;
    const dlrTimestamp = response.dlrTimestamp;

    this.smppResponseUnavailable = false;
    if (!smppSubmittedTimestamp) {
      this.smppChartMessageTitle = 'No SMPP latency data available for this test.';
      this.smppChartMessageDesc = null;
      this.smppChartOptions = null;
      return;
    }

    const submitSM = moment.duration(moment(smppSubmittedTimestamp).diff(moment(testCreatedTimestamp))).asSeconds();
    const submitSMResponse = smppResponseTimestamp
      ? moment.duration(moment(smppResponseTimestamp).diff(moment(smppSubmittedTimestamp))).asSeconds()
      : null;
    const dlrStartTimestamp = smppResponseTimestamp || smppSubmittedTimestamp;
    const dlr = dlrTimestamp ? moment.duration(moment(dlrTimestamp).diff(moment(dlrStartTimestamp))).asSeconds() : null;

    if (
      !Number.isFinite(submitSM) ||
      submitSM < 0 ||
      (submitSMResponse !== null && (!Number.isFinite(submitSMResponse) || submitSMResponse < 0)) ||
      (dlr !== null && (!Number.isFinite(dlr) || dlr < 0))
    ) {
      this.smppChartMessageTitle = 'No SMPP latency data available for this test.';
      this.smppChartMessageDesc = null;
      this.smppChartOptions = null;
      return;
    }

    this.smppChartMessageTitle = null;
    this.smppResponseUnavailable = !smppResponseTimestamp;
    const series: Highcharts.SeriesOptionsType[] = [];

    if (dlr !== null) {
      series.push({
        name: 'deliver_sm',
        type: 'bar',
        color: '#59c4ff',
        data: [dlr],
        tooltip: {
          pointFormat: `
                        <b>Interval: </b> ${dlr}s <br>
                        <b>Timestamp: </b> ${dlrTimestamp}`,
        },
      });
    }

    if (submitSMResponse !== null) {
      series.push({
        name: 'submit_sm response',
        type: 'bar',
        color: '#007aff',
        data: [submitSMResponse],
        tooltip: {
          pointFormat: `
                        <b>Interval: </b> ${submitSMResponse}s <br>
                        <b>Timestamp: </b> ${smppResponseTimestamp}`,
        },
      });
    }

    series.push({
      name: 'submit_sm',
      type: 'bar',
      color: '#033dab',
      data: [submitSM],
      tooltip: {
        pointFormat: `
                        <b>Interval: </b> ${submitSM}s <br>
                        <b>Timestamp: </b> ${smppSubmittedTimestamp}`,
      },
    });

    this.smppChartOptions = {
      credits: { enabled: false },
      chart: { type: 'bar', backgroundColor: 'transparent' },
      title: {
        text: 'SMPP Latency Details',
      },
      tooltip: {
        split: true,
        positioner: function (labelWidth, labelHeight, point) {
          return {
            x: point.plotX / 2.5,
            y: point.plotY - labelHeight / 2.5,
          };
        },
      },
      plotOptions: {
        series: {
          stacking: 'normal',
        },
      },
      xAxis: {
        categories: ['SMSC'],
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Seconds',
        },
        stackLabels: {
          enabled: true,
          format: '{total} s',
        },
      },
      legend: {
        reversed: true,
      },
      series,
    };
  }

  populateSmsChart(event: CellClickedEvent, response): void {
    const testCreatedTimestamp = event.data.createdAt;
    const smscTimestamp = response.smscTimestamp;
    const phoneTimestamp = response.phoneTimestamp;
    const receivedSmsTimestamp = response.receivedSmsTimestamp;

    if (!phoneTimestamp || !receivedSmsTimestamp) {
      this.smsChartMessageTitle = 'No data available for this test.';
      return;
    }
    const submittedBySmsc = !smscTimestamp ? 0 : moment.duration(moment(smscTimestamp).diff(moment(testCreatedTimestamp))).asSeconds();
    // If the smscTimestamp is null, it's probably because the test was not delivered via SMS, so we ignore it
    const receivedOnHandset = moment.duration(moment(phoneTimestamp).diff(moment(!smscTimestamp ? testCreatedTimestamp : smscTimestamp))).asSeconds();
    const receivedOnServer = moment.duration(moment(receivedSmsTimestamp).diff(moment(phoneTimestamp))).asSeconds();

    if (submittedBySmsc < 0 || receivedOnHandset < 0 || receivedOnServer < 0) {
      this.smsChartMessageTitle = 'No data available for this test.';
      return;
    }

    this.smsChartOptions = {
      credits: { enabled: false },
      chart: { type: 'bar', backgroundColor: 'transparent' },
      title: {
        text: 'Received SMS Time Details',
      },
      tooltip: {
        split: true,
        positioner: function (labelWidth, labelHeight, point) {
          return {
            x: point.plotX / 2.5,
            y: point.plotY - labelHeight / 2.5,
          };
        },
      },
      plotOptions: {
        series: {
          stacking: 'normal',
        },
      },
      xAxis: {
        // This two property below is to align both graphs .. SMPP and SMS ...
        offset: 9,
        lineWidth: 0,
        categories: ['SMS'],
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Time in Seconds',
        },
        stackLabels: {
          enabled: true,
          format: '{total} s',
        },
      },
      legend: {
        reversed: true,
      },
      series: [
        {
          name: 'Received on Server',
          type: 'bar',
          color: '#AFE1AF',
          data: [receivedOnServer],
          tooltip: {
            pointFormat: `
                            <b>Interval: </b> ${receivedOnServer}s <br>
                            <b>Timestamp: </b> ${receivedSmsTimestamp}`,
          },
        },
        {
          name: 'Received on Phone*',
          type: 'bar',
          color: '#5fc297',
          data: [receivedOnHandset],
          tooltip: {
            pointFormat: `
                            <b>Interval: </b> ${receivedOnHandset}s <br>
                            <b>Timestamp: </b> ${phoneTimestamp}`,
          },
        },
        {
          name: 'SMSC Timestamp*',
          type: 'bar',
          color: '#18a689',
          data: [submittedBySmsc],
          tooltip: {
            pointFormat: `
                            <b>Interval: </b> ${submittedBySmsc}s <br>
                            <b>Timestamp: </b> ${smscTimestamp}`,
          },
        },
      ],
    };
  }

  getFormattedData(details: TestDetails[]): TestDetails[] {
    details.forEach(section => {
      section.data = section.data.map(detail => {
        const [first, ...rest] = detail.split(':');
        return [first, rest.join(':')];
      });
    });
    return details;
  }

  onSectionChange(sectionId: string) {
    this.currentSection = sectionId ? sectionId : this.testCaseDetails[0].id;
  }

  scrollTo(sectionId: string) {
    document.querySelector('#' + sectionId).scrollIntoView();
    setTimeout(() => {
      // Set timeout necessary because scrollspy will trigger on section change when document scrollBy is triggered...
      this.onSectionChange(sectionId);
    }, 0);
  }

  // ng grid

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.addHorizontalScrollOnTop();
    const columnState = this.localStorage.get(`lnt_table_state_${this.user.id}`);
    this.update();
    this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
  }

  onRowSelected() {
    const selectedRows = this.gridApi.getSelectedRows();
    this.batchSelectedIds = new Set(selectedRows.map(row => row.id));
    this.changeBatch.emit(selectedRows);
  }

  resetBatch() {
    this.batchSelectedIds.clear();
    this.gridApi.deselectAll();
  }

  getContextMenuItems = (params: GetContextMenuItemsParams<TestResultAction>): MenuItemDef[] => {
    const currentRowClicked = params.node.data;
    const phoneReportState = PhoneReportUtils.getPhoneReportState({
      test: currentRowClicked,
      phoneReportsInfo: this.phoneReportsInfo,
      currentUser: this.user,
      isPhoneReportsLoading: this.isPhoneReportsLoading,
      testType: PhoneReportTestType.LNT,
    });

    const result: MenuItemDef[] = [
      {
        name: 'Test Case Details ',
        icon: '<span class="icon-tests-settings fs-4"></span>',
        action: () => {
          this.service.announceAction({ name: 'copy', row: currentRowClicked, column: 'action' });
        },
      },
      {
        name: 'Repeat batch',
        icon: '<span class="icon-refresh fs-4"></span>',
        action: () => {
          this.onAction({ name: 'repeat', row: currentRowClicked, column: 'action' });
        },
      },
      {
        name: 'Copy',
        icon: '<span class="icon-copy fs-4"></span>',
        action: () => {
          const renderedValue = this.getRenderedCellValue(params);
          this.clipBoard.copy(renderedValue ? renderedValue : currentRowClicked.id.toString());
        },
      },
      {
        name: 'Show/Add comment',
        icon: '<span class="icon-comment fs-4"></span>',
        action: () => {
          this.onAction({ name: 'comment', row: currentRowClicked, column: 'action' });
        },
      },
      {
        name: phoneReportState.canOpenReport ? 'Show number report' : 'Phone number report',
        icon: '<span class="icon-bug ms-0_2 fs-4"></span>',
        disabled: !phoneReportState.isAllowed && !phoneReportState.canOpenReport,
        tooltip: phoneReportState.tooltip,
        action: () => {
          if (phoneReportState.isAllowed) {
            this.onAction({ name: 'addPhoneNumberReport', row: currentRowClicked, column: 'action' });
            return;
          }

          if (phoneReportState.canOpenReport) {
            this.onAction({ name: 'showPhoneNumberReport', row: currentRowClicked, column: 'action' });
          }
        },
      },
      {
        name: 'Reimburse test',
        icon: '<span class="far fa-money-bill-alt ms-0_2 fs-4"></span>',
        disabled: !ReimburseUtils.canReimburse(currentRowClicked),
        action: () => {
          this.onAction({ name: 'reimburse', row: currentRowClicked, column: 'action' });
        },
      },
    ];
    if (this.isAdmin) {
      return result;
    }
    return result.filter(item => item.name !== 'Reimburse test');
  };

  onAfterPhoneReportCreate(phoneReportModel: PhoneReportCreate) {
    this.phoneReportsInfo = [
      ...this.phoneReportsInfo,
      {
        testId: phoneReportModel.testId,
        testType: phoneReportModel.testType,
      },
    ];
  }

  ngOnDestroy() {
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }
    if (this.phoneReportsSubscription) {
      this.phoneReportsSubscription.unsubscribe();
    }
    this.actionSubscription.unsubscribe();
    this.columnChange$.unsubscribe();
    this.mutationObserver.disconnect();
    if (this.statusUpdates$) {
      this.statusUpdates$.unsubscribe();
    }
    this.liveRealtimeService.close();
  }

  private openPhoneReport(testId?: number) {
    if (!testId) {
      return;
    }
    const url = `${BrowserUtils.getHostUrl()}/#/other/reported-numbers?search=${testId}`;
    window.open(url, '_blank', 'noopener');
  }
}
